import "../src/assets/scss/main.scss";

import { useContext, useEffect } from "react";
import { Route, Routes, useLocation } from "react-router-dom";

import { RequireRole } from "./components/RequireRole";
import { AccountContainer } from "./container/AccountContainer";
import { TournamentContainer } from "./container/TournamentContainer";
import { EuserContext } from "./data/providers/EuserProvider";
import { RedirectToRecentTournament } from "./navs/RedirectToRecentTournament";
import { AccountEdit } from "./pages/AccountEdit";
import { AccountRecover } from "./pages/AccountRecover";
import { AccountReservations } from "./pages/AccountReservations";
import { AccountSignIn } from "./pages/AccountSignIn";
import { AccountSignUp } from "./pages/AccountSignUp";
import { AccountTickets } from "./pages/AccountTickets";
import { CheckInPage } from "./pages/CheckInPage";
import { ContentPage } from "./pages/ContentPage";
import { CreateTicketCategoryPage } from "./pages/CreateTicketCategoryPage";
import { CreateTicketPage } from "./pages/CreateTicketPage";
import { CreateTournamentPage } from "./pages/CreateTournamentPage";
import { CreateUserOrSponsorPage } from "./pages/CreateUserOrSponsorPage";
import { CreateUserSponsorLinkingPage } from "./pages/CreateUserSponsorLinkingPage";
import { DataExportPage } from "./pages/DataExportPage";
import { EditRoundPage } from "./pages/EditRoundPage";
import { EditTicketCategoryPage } from "./pages/EditTicketCategoryPage";
import { EditTournamentPage } from "./pages/EditTournamentPage";
import { EditUserOrSponsorPage } from "./pages/EditUserOrSponsorPage";
import { ListTournamentsPage } from "./pages/ListTournamentsPage";
import { NotCheckedInAttendeesPage } from "./pages/NotCheckedInAttendeesPage";
import { NotFoundPage } from "./pages/NotFoundPage";
import { ScheduleRoundsPage } from "./pages/ScheduleRoundsPage";
import { TicketCategoryPage } from "./pages/TicketCategoryPage";
import { TicketGroupPage } from "./pages/TicketGroupPage";
import { TicketsPage } from "./pages/TicketsPage";
import { TournamentPreviewPage } from "./pages/TournamentPreviewPage";
import { TournamentRegisterPage } from "./pages/TournamentRegisterPage";
import { UploadBannerImagePage } from "./pages/UploadBannerImagePage";
import { UsersOrSponsorsPage } from "./pages/UsersOrSponsorsPage";
import { notify } from "./utils/notify";

function App() {
  const location = useLocation();
  const { euserIsAdmin, euserIsEditor, euserIsVolunteer } =
    useContext(EuserContext);
  // to show persisted notifications on navigation and page load
  useEffect(() => {
    const showToast = async () => {
      await notify.showPersistedToast();
    };
    showToast();
  }, [location.key]);
  return (
    <Routes>
      <Route path="/" element={<RedirectToRecentTournament />}></Route>

      <Route path="/tournaments" element={<TournamentContainer />}>
        <Route index element={<ListTournamentsPage />}></Route>

        <Route
          path=":tournamentId/register"
          element={<TournamentRegisterPage />}
        ></Route>

        {/* Admin or Content Editor */}
        <Route
          element={
            <RequireRole check={() => euserIsAdmin() || euserIsEditor()} />
          }
        >
          <Route
            path=":tournamentId/edit"
            element={<EditTournamentPage />}
          ></Route>
        </Route>

        {/* Admin or Volunteer */}
        <Route
          element={
            <RequireRole check={() => euserIsAdmin() || euserIsVolunteer()} />
          }
        >
          <Route
            path=":tournamentId/admin/check-in"
            element={<CheckInPage />}
          ></Route>
          <Route
            path=":tournamentId/admin/NotCheckedInAttendeesPage/:roundId"
            element={<NotCheckedInAttendeesPage />}
          ></Route>
        </Route>

        {/* Authenticated — any role (used for self-registration by non-admin users) */}
        <Route element={<RequireRole check={() => true} />}>
          <Route
            path=":tournamentId/admin/tickets/:attendeeOrSponsor/new"
            element={<CreateTicketPage />}
          ></Route>
        </Route>

        {/* Admin or Content Editor */}
        <Route
          element={
            <RequireRole check={() => euserIsAdmin() || euserIsEditor()} />
          }
        >
          {/* Banner Image */}
          <Route
            path=":tournamentId/admin/banner-image"
            element={<UploadBannerImagePage />}
          ></Route>

          {/* content */}
          <Route
            path=":tournamentId/admin/content/widgets"
            element={<ContentPage />}
          ></Route>
        </Route>

        {/* Admin only */}
        <Route element={<RequireRole check={euserIsAdmin} />}>
          <Route path="new" element={<CreateTournamentPage />}></Route>

          {/* ticket category */}
          <Route
            path=":tournamentId/admin/ticket_categories/:attendeeOrSponsor"
            element={<TicketCategoryPage />}
          ></Route>
          <Route
            path=":tournamentId/admin/ticket_categories/:attendeeOrSponsor/new"
            element={<CreateTicketCategoryPage />}
          ></Route>
          <Route
            path=":tournamentId/admin/ticket_categories/:attendeeOrSponsor/:ticketCategoryId/edit"
            element={<EditTicketCategoryPage />}
          ></Route>

          {/* schedule rounds */}
          <Route
            path=":tournamentId/admin/rounds/new"
            element={<ScheduleRoundsPage />}
          ></Route>
          <Route
            path=":tournamentId/admin/rounds/:roundId/edit"
            element={<EditRoundPage />}
          ></Route>

          {/* tickets */}
          <Route
            path=":tournamentId/admin/tickets/:attendeeOrSponsor"
            element={<TicketsPage />}
          ></Route>
          <Route
            path=":tournamentId/admin/tickets/:attendeeOrSponsor/:ticketId/groups"
            element={<TicketGroupPage />}
          ></Route>

          {/* data export */}
          <Route
            path=":tournamentId/admin/data-export"
            element={<DataExportPage />}
          ></Route>

          {/* user/sponsor - UsersOrSponsorsPage uses a query string param for default tab selection (e.g. ?tab=sponsor or ?tab=user). */}
          <Route
            path=":tournamentId/admin/user-sponsor"
            element={<UsersOrSponsorsPage />}
          ></Route>
          <Route
            path=":tournamentId/admin/user-sponsor/new"
            element={<CreateUserOrSponsorPage />}
          ></Route>
          <Route
            path=":tournamentId/admin/user-sponsor/:userOrSponsorId/edit"
            element={<EditUserOrSponsorPage />}
          ></Route>
          <Route
            path=":tournamentId/admin/userSponsor/new"
            element={<CreateUserSponsorLinkingPage />}
          ></Route>
        </Route>
      </Route>

      <Route
        path="/tournaments/:tournamentId/:uniqueSlug"
        element={<TournamentPreviewPage />}
      ></Route>

      <Route path="/account" element={<AccountContainer />}>
        <Route path="/account/signin" element={<AccountSignIn />}></Route>

        <Route path="/account/signup" element={<AccountSignUp />}></Route>

        <Route path="/account/recover" element={<AccountRecover />}></Route>

        <Route path="/account/edit" element={<AccountEdit />}></Route>

        <Route
          path="/account/reservations"
          element={<AccountReservations />}
        ></Route>

        <Route path="/account/tickets" element={<AccountTickets />}></Route>

        <Route
          path="/account/tickets/:ticketId/groups"
          element={<TicketGroupPage />}
        ></Route>

        <Route
          path="/account/sponsor/:userOrSponsorId/edit"
          element={<EditUserOrSponsorPage />}
        ></Route>
      </Route>

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

export default App;
